import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FormField } from "@/components/ui/FormField";
import { useLogin } from "@/features/auth/useAuth";
import { getApiErrorMessage } from "@/api/client";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type FormValues = z.infer<typeof schema>;

export default function Login() {
  const login = useLogin();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4">
      <div className="w-full max-w-sm card p-8">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-teal-500 text-base font-bold text-cream-50">
            E
          </div>
          <div>
            <p className="text-lg font-semibold text-ink">
              Eyy <span className="text-gold-600">Admin</span>
            </p>
            <p className="text-xs text-ink-faint">Ops · KYC · Commission · Fares</p>
          </div>
        </div>

        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit((values) => login.mutate(values))}
        >
          <FormField label="Email" htmlFor="email" error={errors.email?.message}>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              placeholder="you@eyyauto.in"
              error={!!errors.email}
              {...register("email")}
            />
          </FormField>

          <FormField label="Password" htmlFor="password" error={errors.password?.message}>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              error={!!errors.password}
              {...register("password")}
            />
          </FormField>

          {login.isError && (
            <p className="text-sm text-danger-500">{getApiErrorMessage(login.error)}</p>
          )}

          <Link to="/forgot-password" className="-mt-2 self-end text-sm text-teal-600 hover:underline">
            Forgot password?
          </Link>

          <Button type="submit" variant="primary" size="lg" loading={login.isPending} className="mt-2">
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
